import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PAGE_SIZES } from '@/models/listPage';

/** Bas de tableau d'une liste paginée côté API : « Affichage de 11 à 20 sur 42 utilisateurs », taille de page, précédent / suivant */
export function Pagination({
  page,
  itemsPerPage,
  totalItems,
  onPageChange,
  onItemsPerPageChange,
  label,
  feminine = false,
  pageSizes = PAGE_SIZES,
}: {
  page: number;
  itemsPerPage: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (itemsPerPage: number) => void;
  /** Libellé des éléments, au singulier et au pluriel */
  label: [string, string];
  /** Libellé féminin : « Aucune feuille » (sinon « Aucun utilisateur ») */
  feminine?: boolean;
  /** Tailles de page proposées (PAGE_SIZES par défaut ; ex. multiples de 3 pour une grille de cartes) */
  pageSizes?: readonly number[];
}) {
  const pageCount = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const first = totalItems === 0 ? 0 : (page - 1) * itemsPerPage + 1;
  const last = Math.min(page * itemsPerPage, totalItems);

  return (
    <div className="px-4 sm:px-6 py-3 sm:py-4 border-t border-slate-200 bg-slate-50/50 flex flex-col sm:flex-row gap-3 items-center justify-between">
      <p className="text-sm text-slate-500" data-testid="pagination-summary">
        {totalItems === 0 ? (
          `${feminine ? 'Aucune' : 'Aucun'} ${label[0]}`
        ) : (
          <>
            Affichage de <span className="font-medium">{first}</span> à <span className="font-medium">{last}</span> sur{' '}
            <span className="font-medium">{totalItems}</span> {totalItems > 1 ? label[1] : label[0]}
          </>
        )}
      </p>
      {/* Mobile : taille de page à gauche, ‹ Page x sur y › à droite, sur une ligne (libellés des flèches pour les
          lecteurs d'écran seulement) */}
      <div className="w-full sm:w-auto flex items-center justify-between gap-3">
        <select
          aria-label="Éléments par page"
          value={itemsPerPage}
          onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
          className="h-9 sm:h-auto px-2 py-1 border border-slate-300 rounded-md text-sm bg-card text-slate-700"
        >
          {pageSizes.map((size) => (
            <option key={size} value={size}>
              {size} par page
            </option>
          ))}
        </select>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="h-9 sm:h-8"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
          >
            <ChevronLeft className="w-4 h-4" />
            <span className="sr-only sm:not-sr-only">Précédent</span>
          </Button>
          <span className="text-sm text-slate-500 whitespace-nowrap tabular-nums" data-testid="pagination-page">
            Page {page} sur {pageCount}
          </span>
          <Button
            variant="outline"
            size="sm"
            className="h-9 sm:h-8"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= pageCount}
          >
            <span className="sr-only sm:not-sr-only">Suivant</span>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
